import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useUserProfile } from '@/hooks/useUserProfile';
import { AvatarCreator } from './AvatarCreator';
import { AvatarDisplay } from './AvatarDisplay';
import { generateAvatarFromUsername, DEFAULT_AVATAR_CONFIG, type AvatarConfig } from '@/lib/avatar-config';
import { toast } from 'sonner';
import { CheckCircle, User, Sparkles } from 'lucide-react';

interface AvatarOnboardingProps {
  onComplete?: () => void;
  onSkip?: () => void;
}

export const AvatarOnboarding = ({ onComplete, onSkip }: AvatarOnboardingProps) => {
  const { profile, updateUsername, updateAvatar } = useUserProfile();
  const [step, setStep] = useState<'welcome' | 'username' | 'avatar' | 'complete'>('welcome');
  const [username, setUsername] = useState('');
  const [avatarConfig, setAvatarConfig] = useState<AvatarConfig>(DEFAULT_AVATAR_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setUsernameError('');
  };

  const validateUsername = (username: string): boolean => {
    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return false;
    }
    if (username.length > 20) {
      setUsernameError('Username must be less than 20 characters');
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameError('Username can only contain letters, numbers, and underscores');
      return false;
    }
    return true;
  };

  const handleUsernameSubmit = async () => {
    if (!validateUsername(username)) return;
    
    setIsLoading(true);
    const { error } = await updateUsername(username);
    
    if (error) {
      setUsernameError(error);
      setIsLoading(false);
      return;
    }

    // Generate avatar from username
    const generatedConfig = generateAvatarFromUsername(username);
    setAvatarConfig(generatedConfig);
    setStep('avatar');
    setIsLoading(false);
  };

  const handleAvatarSave = async (config: AvatarConfig) => {
    setIsLoading(true);
    const { error } = await updateAvatar('custom', config);
    
    if (error) {
      toast.error('Failed to save avatar');
      setIsLoading(false);
      return;
    }

    toast.success('Profile created successfully!');
    setStep('complete');
    setIsLoading(false);
    
    setTimeout(() => {
      onComplete?.();
    }, 1500);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teen-blue/20 via-background to-teen-purple/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-teen-blue to-teen-purple rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Welcome to ChessHatch! 🎉</CardTitle>
              <CardDescription className="mt-2">
                Let's create your gaming profile so you can battle in style
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Choose a unique username and design your avatar to represent you in games
              </p>
            </div>
            <Separator />
            <div className="space-y-3">
              <Button onClick={() => setStep('username')} className="w-full bg-teen-blue hover:bg-teen-blue/90">
                <User className="w-4 h-4 mr-2" />
                Create My Profile
              </Button>
              <Button variant="outline" onClick={handleSkip} className="w-full">
                Skip for Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'username') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teen-blue/20 via-background to-teen-purple/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Choose Your Username</CardTitle>
            <CardDescription>
              This is how other players will know you
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="Enter your gaming name"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUsernameSubmit()}
                className={usernameError ? 'border-destructive' : ''}
              />
              {usernameError && (
                <p className="text-sm text-destructive">{usernameError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                3-20 characters, letters, numbers, and underscores only
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setStep('welcome')} className="flex-1">
                Back
              </Button>
              <Button 
                onClick={handleUsernameSubmit} 
                disabled={!username || isLoading}
                className="flex-1"
              >
                {isLoading ? 'Checking...' : 'Continue'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'avatar') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teen-blue/20 via-background to-teen-purple/20 p-4 py-8">
        <AvatarCreator
          initialConfig={avatarConfig}
          username={username}
          onSave={handleAvatarSave}
          onCancel={() => setStep('username')}
        />
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teen-blue/20 via-background to-teen-purple/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto">
              <CheckCircle className="w-16 h-16 text-teen-green mx-auto mb-4" />
            </div>
            <div>
              <CardTitle className="text-2xl text-teen-green">Profile Created! 🎉</CardTitle>
              <CardDescription>
                Your gaming profile is ready to go
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <AvatarDisplay
              username={username}
              avatarConfig={avatarConfig}
              size="xl"
              showAnimation={true}
              className="mx-auto"
            />
            <div>
              <p className="font-semibold text-lg">{username}</p>
              <p className="text-muted-foreground">Ready to play!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};